// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// Didn't bring the 2024 - sep upgrade, because we are using this widget as a component, so there is no CMS control on it's actions
import cancelApproval from "@insite/client-framework/Store/Pages/Cart/Handlers/CancelApproval";
import discardEdit from "@insite/client-framework/Store/Pages/Cart/Handlers/DiscardEdit";

import RequestCerificationsModal from "@eisinc/App/Components/RequestCerificationsModal/RequestCerificationsModal";
import { getPageLinkByPageType } from "@eisinc/App/Store/Selectors/LinksSelectors";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import TwoButtonModal from "@eisinc/Overrides/Components/TwoButtonModal";
import CartSaveOrderButton from "@eisinc/Overrides/Widgets/Cart/CartSaveOrderButton";
import QuestionCircle from "@eisinc/XDL/Icons/QuestionCircle";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canAddAllToList,
    canSaveOrder,
    getCurrentCartState,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        cart,
        isCartEmpty: isCartEmpty(cart),
        canSaveOrder: canSaveOrder(cart),
        canAddAllToList: canAddAllToList(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        isApprovalEditDraft: cart?.status === "AwaitingApprovalEditDraft",
        isApprovalInProcess: cart?.orderApprovalStatus === "ApprovalInProcess",
        orderApprovalDetailsPageUrl: getPageLinkByPageType(state, "OrderApprovalDetailsPage")?.url,
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    discardEdit,
    cancelApproval,
    // XNG-Changes
    clearCurrentCart,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartActionsStyles {
    narrowHidden?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderClickable?: ClickablePresentationProps;
    addAllToListClickable?: ClickablePresentationProps;
    requestAQuoteClickable?: ClickablePresentationProps;
    checkoutClickable?: ClickablePresentationProps;
    wideHidden?: HiddenProps;
    wideOverflowMenu?: OverflowMenuPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    discardEditWrapper?: InjectableCss;
    discardEditButton?: ButtonPresentationProps;
    cancelApprovalWrapper?: InjectableCss;
    cancelApprovalButton?: ButtonPresentationProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    addAllToListLink?: LinkPresentationProps;
    removeAllLink?: LinkPresentationProps;
    requestCertificationsWrapper?: InjectableCss;
    requestCertificationsLink?: LinkPresentationProps;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    removeModalButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    removeButton?: ButtonPresentationProps;
    discardEditLink?: LinkPresentationProps;
    cancelApprovalLink?: LinkPresentationProps;
}

export const cartActionsStyles: CartActionsStyles = {
    narrowHidden: {
        above: "sm",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    wideHidden: {
        below: "md",
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    wideOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 20px;
            `,
        },
    },
    discardEditWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    discardEditButton: {
        variant: "secondary",
    },
    cancelApprovalWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    cancelApprovalButton: {
        variant: "secondary",
    },
};

const styles = cartActionsStyles;

const CartActions: FC<Props> = ({
    cart,
    isCartEmpty,
    canAddAllToList,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    isPunchOutOrder,
    isApprovalEditDraft,
    history,
    orderApprovalDetailsPageUrl,
    discardEdit,
    isApprovalInProcess,
    cancelApproval,
    // XNG-Changes
    clearCurrentCart,
}) => {
    const [discardChangesModalIsOpen, setDiscardChangesModalIsOpen] = useState(false);

    // <XNG-Changes>
    const [isOpenModal, setIsOpenModal] = React.useState(false);
    const [requestCertificationsModalIsOpen, setRequestCertificationsModalIsOpen] = useState(false);
    // </XNG-Changes>

    const addAllToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfos =
            cart?.cartLines
                ?.filter(o => o.productId && o.qtyOrdered)
                .map(cartLine => ({
                    productId: cartLine.productId!,
                    qtyOrdered: cartLine.qtyOrdered!,
                    unitOfMeasure: cartLine.unitOfMeasure,
                })) || [];

        // if (!wishListSettings.allowMultipleWishLists) {
        //     addToWishList({
        //         productInfos,
        //         onSuccess: () => {
        //             toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
        //         },
        //         onComplete(resultProps) {
        //             if (resultProps.result?.wishList) {
        //                 // "this" is targeting the object being created, not the parent SFC
        //                 // eslint-disable-next-line react/no-this-in-sfc
        //                 this.onSuccess?.(resultProps.result.wishList);
        //             } else if (resultProps.result?.errorMessage) {
        //                 toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
        //             }
        //         },
        //     });
        //     return;
        // }

        // XNG-Change: pass showProductsSelection
        setAddToListModalIsOpen({ modalIsOpen: true, productInfos, showProductsSelection: true } as any);
    };
    const closeConfirmationModalHandler = () => {
        setIsOpenModal(false);
    };
    if (!cart || isPunchOutOrder || isCartEmpty) {
        return null;
    }

    // XNG-Change
    const closeModalHandler = () => {
        setRequestCertificationsModalIsOpen(false);
    };

    if (isApprovalEditDraft) {
        const discardEditClickHandler = async () => {
            discardEdit({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.orderNumber.split("_")[1]}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.discardEditWrapper}>
                {/* XNG-Changes */}
                <Link
                    {...styles.discardEditLink}
                    onClick={() => setDiscardChangesModalIsOpen(true)}
                    data-test-selector="cartTotal_discardEdit"
                >
                    {translate("Discard edit")}
                </Link>
                <TwoButtonModal
                    headlineText={translate("Are you sure?")}
                    modalIsOpen={discardChangesModalIsOpen}
                    messageText={siteMessage("OrderApproval_DiscardEditConfirmationMessage")}
                    cancelButtonText={translate("Continue editing")}
                    submitButtonText={translate("Discard edit")}
                    onCancel={() => setDiscardChangesModalIsOpen(false)}
                    onSubmit={discardEditClickHandler}
                    dataTestSelector="discard-edit-modal"
                    cancelTestSelector="discard-edit-modal_cancel"
                    submitTestSelector="discard-edit-modal_discard"
                ></TwoButtonModal>
            </StyledWrapper>
        );
    }

    if (isApprovalInProcess) {
        const cancelApprovalClickHandler = async () => {
            cancelApproval({
                cart,
                onSuccess: () => {
                    if (orderApprovalDetailsPageUrl) {
                        history.push(`${orderApprovalDetailsPageUrl}?cartId=${cart.id}`);
                    }
                },
            });
        };

        return (
            <StyledWrapper {...styles.cancelApprovalWrapper}>
                {/* XNG-Changes */}
                <Link
                    {...styles.cancelApprovalLink}
                    onClick={cancelApprovalClickHandler}
                    data-test-selector="cartTotal_saveAndContinueLater"
                >
                    {translate("Save and continue later")}
                </Link>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="cartActionsNarrow">
            <StyledWrapper {...styles.leftSection}>
                <CartSaveOrderButton variant="link" />
                <Link
                    data-test-selector="cartActionsAddAllToList"
                    onClick={addAllToListClickHandler}
                    {...styles.addAllToListLink}
                    disabled={!canAddAllToList}
                    key="addAll"
                >
                    {translate("Add Products to Shopping List")}
                </Link>
                <StyledWrapper {...styles.requestCertificationsWrapper}>
                    <Link
                        {...styles.requestCertificationsLink}
                        onClick={() => {
                            setRequestCertificationsModalIsOpen(true);
                        }}
                    >
                        {translate("Request Certifications")}
                    </Link>
                    <Tooltip
                        text={siteMessage("Custom_Request_Certifications_Message")}
                        iconProps={{ src: QuestionCircle, ...{ width: 12, height: 13 }, color: "primary" }}
                    />
                    <RequestCerificationsModal
                        cart={cart}
                        closeModalHandler={closeModalHandler}
                        isOpen={requestCertificationsModalIsOpen}
                    />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <Link
                    onClick={() => setIsOpenModal(true)}
                    {...styles.removeAllLink}
                    data-test-selector="cartlineHeader_removeAll"
                >
                    {translate("Remove All")}
                </Link>
                <Modal
                    headline={translate("Confirm Deletion")}
                    isOpen={isOpenModal}
                    handleClose={closeConfirmationModalHandler}
                    sizeVariant="small"
                >
                    <StyledWrapper>
                        {translate("Are you sure you want to remove all items from your shopping cart?")}
                    </StyledWrapper>
                    <StyledWrapper {...styles.removeModalButtonsWrapper} className="modal-actions">
                        <Button {...styles.cancelButton} onClick={closeConfirmationModalHandler}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.removeButton}
                            onClick={() => {
                                clearCurrentCart();
                                setIsOpenModal(false);
                            }}
                        >
                            {translate("Remove All")}
                        </Button>
                    </StyledWrapper>
                </Modal>
            </StyledWrapper>
        </StyledWrapper>
    );
};

// omitting these props from the widget in order to use it as a component
type CartActionsButtonsProp = Omit<
    typeof CartActions,
    "id" | "type" | "zone" | "parentId" | "generalFields" | "translatableFields" | "contextualFields" | "fields"
>;

// using this widget as a component
export const CartActionButtons: CartActionsButtonsProp & any = connect(
    mapStateToProps,
    mapDispatchToProps,
)(CartActions);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartActions)),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
